import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { emailRe, rateLimited } from "@/lib/support.shared";


const supportSchema = () => z.object({
  name: z.string().trim().min(2).max(120),
  email: z.string().trim().toLowerCase().regex(emailRe).max(200),
  orderRef: z.string().trim().max(80).optional().or(z.literal("")),
  topic: z.string().trim().min(2).max(60),
  message: z.string().trim().min(10).max(4000),
  /** Campo trampa antispam: debe llegar vacío. */
  website: z.string().max(200).optional().or(z.literal("")),
});

export const submitSupportRequest = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => supportSchema().parse(input))
  .handler(async ({ data }) => {
    if (data.website) return { ok: true as const, caseNumber: "SOP-000000" };
    if (rateLimited(`sup:${data.email}`)) {
      return {
        ok: false as const,
        error: "Recibimos varias consultas tuyas hace un momento. Probá de nuevo más tarde.",
      };
    }

    const { supabaseAdmin } = await import("@/integrations/supabase/client.target.server");
    const { data: row, error } = await supabaseAdmin
      .from("shop_support_requests")
      .insert({
        name: data.name,
        email: data.email,
        order_ref: data.orderRef || null,
        topic: data.topic,
        message: data.message,
      })
      .select("case_number")
      .single();

    if (error || !row) {
      console.error("[soporte] no se pudo registrar la consulta", error);
      return { ok: false as const, error: "No pudimos registrar tu consulta. Intentá de nuevo." };
    }

    try {
      const { sendSupportNotification } = await import("@/lib/support.server");
      await sendSupportNotification({ ...data, caseNumber: row.case_number });
    } catch (err) {
      console.error("[soporte] aviso por email no enviado", err);
    }

    return { ok: true as const, caseNumber: row.case_number as string };
  });

const refundSchema = () => z.object({
  email: z.string().trim().toLowerCase().regex(emailRe).max(200),
  orderRef: z.string().trim().max(80).optional().or(z.literal("")),
  product: z.string().trim().max(200).optional().or(z.literal("")),
  purchaseDate: z.string().trim().max(20).optional().or(z.literal("")),
  reason: z.string().trim().max(4000).optional().or(z.literal("")),
  website: z.string().max(200).optional().or(z.literal("")),
});

export const submitRefundRequest = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => refundSchema().parse(input))
  .handler(async ({ data }) => {
    if (data.website) return { ok: true as const, requestNumber: "ARR-000000" };
    if (rateLimited(`arr:${data.email}`)) {
      return {
        ok: false as const,
        error: "Ya registramos varias solicitudes con este email. Escribinos por soporte.",
      };
    }

    const { supabaseAdmin } = await import("@/integrations/supabase/client.target.server");
    const { data: row, error } = await supabaseAdmin
      .from("shop_refund_requests")
      .insert({
        email: data.email,
        order_ref: data.orderRef || null,
        product: data.product || null,
        purchase_date: data.purchaseDate || null,
        reason: data.reason || null,
      })
      .select("request_number")
      .single();

    if (error || !row) {
      console.error("[arrepentimiento] no se pudo registrar la solicitud", error);
      return { ok: false as const, error: "No pudimos registrar tu solicitud. Intentá de nuevo." };
    }

    return { ok: true as const, requestNumber: row.request_number as string };
  });
